import { numberUtils } from '@/utils/number'
import {
  getOverdueOrderList,
  FuzzySearch,
  searchByCustomerNameSensitive,
  searchByIdCard,
  getAllEmployeeList,
} from '@/api/billWarning'
import { useRemoteSearchSelect } from '../../composables/useRemoteSearchSelect'
import {
  RENT_STATUS_GROUP_OPTIONS,
  PAY_MODE_OPTIONS,
  payModeTagType,
  rentStatusTagType,
} from '../../constants'

const yuan = (v) => (v == null || v === '' ? '--' : `¥${numberUtils.format(v)}`)

// 身份证筛选临时字段：因司机姓名与身份证都映射 customerId，身份证用临时 key，dealWithSearch 合并
const ID_CARD_KEY = '_idCardCustomerId'

// h 为 createElement(由页面 setup 传入 proxy.$createElement)。QSCommonTable 直接把列 render
// 当 scopedSlot 返回，不绑 this、不传 h，故列 render 必须用箭头函数闭包捕获此处的 h。
// 列宽用 minWidth(而非固定 width)：el-table fit 会把 min-width 列按比例拉伸撑满容器，
// 使列宽和始终 >= 容器，从而 fixed:'right' 操作列贴右也不留空白、表格撑满(参考 entOrder/primaryOrder)。
export function createListConfig({
  getBlm,
  onDetail,
  onSearch,
  onListLoaded,
  h,
}) {
  // 5 个远程搜索下拉
  const plate = useRemoteSearchSelect({
    label: '车牌',
    prop: 'carId',
    searchApi: FuzzySearch,
    paramKey: 'plateNo',
    extraParams: { state: 1 },
    buildLabel: (r) => r.plateNo || r.name,
    placeholder: '请输入至少4位车牌号',
    minChars: 4,
    getBlm,
  })
  const driver = useRemoteSearchSelect({
    label: '司机姓名',
    prop: 'customerId',
    searchApi: searchByCustomerNameSensitive,
    paramKey: 'customerName',
    extraParams: { mixedType: 'YES' },
    buildLabel: (r) =>
      (r.customerTel ? `${r.customerName}（${r.customerTel}）` : r.customerName),
    getBlm,
  })
  const idCard = useRemoteSearchSelect({
    label: '身份证',
    prop: ID_CARD_KEY,
    searchApi: searchByIdCard,
    paramKey: 'idCardNo',
    buildLabel: (r) => `${r.customerName}（${r.idCardNo}）`,
    placeholder: '请输入完整的身份证号',
    getBlm,
  })
  const supervisor = useRemoteSearchSelect({
    label: '所属车管',
    prop: 'vehicleManagerId',
    searchApi: getAllEmployeeList,
    paramKey: 'userName',
    buildLabel: (r) => r.userName,
    getBlm,
  })
  const manager = useRemoteSearchSelect({
    label: '所属司管',
    prop: 'driverManagerId',
    searchApi: getAllEmployeeList,
    paramKey: 'userName',
    buildLabel: (r) => r.userName,
    getBlm,
  })

  // 司机姓名下拉项两端分布：左姓名、右手机号(灰色)，参考个人租赁订单(同名司机靠手机号区分)
  driver.column.elConfig.options.slotElRender = (r) =>
    '<div style="display:flex;justify-content:space-between;align-items:center;gap:12px">' +
    `<span>${r.customerName || ''}</span>` +
    `<span style="color:#8492a6">${r.customerTel || ''}</span></div>`

  // 司机姓名(customerId)与身份证(_idCardCustomerId)互斥：选中其一清空另一个，以最后一次选择为准
  driver.column.elConfig.events = {
    change: (value) => {
      if (value) {
        getBlm()?.setItemData({
          elConfigKey: 'value',
          key: ID_CARD_KEY,
          data: '',
        })
      }
    },
  }
  idCard.column.elConfig.events = {
    change: (value) => {
      if (value) {
        getBlm()?.setItemData({
          elConfigKey: 'value',
          key: 'customerId',
          data: '',
        })
      }
    },
  }

  const configData = {
    isShowDepartment: false,
    formData: {
      formProps: { model: {} },
      list: [
        plate.column,
        driver.column,
        idCard.column,
        supervisor.column,
        manager.column,
        {
          el: 'select',
          itemConfig: { label: '租赁状态', prop: 'rentStatusGroup' },
          elConfig: {
            clearable: true,
            placeholder: '全部状态',
            options: {
              label: 'label',
              value: 'value',
              list: RENT_STATUS_GROUP_OPTIONS,
            },
          },
        },
        {
          el: 'select',
          itemConfig: { label: '付款方式', prop: 'payMode' },
          elConfig: {
            clearable: true,
            placeholder: '全部方式',
            options: { label: 'label', value: 'value', list: PAY_MODE_OPTIONS },
          },
        },
      ],
      // 每次请求前：合并身份证临时 customerId → customerId，清理临时字段
      dealWithSearch: (params) => {
        const p = { ...params }
        if (p[ID_CARD_KEY]) p.customerId = p[ID_CARD_KEY]
        delete p[ID_CARD_KEY]
        return p
      },
      // 仅点“查询”时触发(翻页不触发)：刷新汇总
      clickSearch: () => {
        if (onSearch) onSearch(getSearchParams(getBlm))
      },
    },
    tableData: {
      // 包装列表接口：透传响应给 BLM(取 items/total)，同时提取 data.extInfo(数据最后更新时间)回传页面
      apiFn: async (params) => {
        const res = await getOverdueOrderList(params)
        if (onListLoaded) onListLoaded(res?.data?.extInfo || '')
        return res
      },
      pagination: { pageSizes: [10, 20, 30, 50] },
      columns: [
        {
          name: 'orderId',
          columnProps: { label: '订单ID', prop: 'orderId', minWidth: 140 },
        },
        {
          name: 'plateNo',
          columnProps: {
            label: '车牌号',
            prop: 'plateNo',
            minWidth: 120,
            wordClass: 'bold-text',
          },
        },
        {
          name: 'customerName',
          columnProps: {
            label: '司机姓名',
            prop: 'customerName',
            minWidth: 100,
          },
        },
        {
          name: 'idCardNo',
          columnProps: { label: '司机身份证', prop: 'idCardNo', minWidth: 180 },
        },
        {
          name: 'customerTel',
          columnProps: {
            label: '司机电话',
            prop: 'customerTel',
            minWidth: 130,
          },
        },
        {
          name: 'contract',
          columnProps: { label: '合同周期', minWidth: 200 },
          getText: ({ row }) =>
            (row.contractStartDate && row.contractEndDate
              ? `${row.contractStartDate} ~ ${row.contractEndDate}`
              : row.contractPeriod || '--'),
        },
        {
          name: 'maxOverdueDays',
          columnProps: { label: '逾期天数', minWidth: 100, align: 'center' },
          render: (scope) =>
            h(
              'span',
              { style: 'color:#ff4d4f;font-weight:bold' },
              `${scope.row.maxOverdueDays ?? '--'}天`,
            ),
        },
        {
          name: 'overdueRentAmount',
          columnProps: { label: '逾期租金', minWidth: 120, align: 'right' },
          render: (scope) =>
            h(
              'span',
              { style: 'color:#ff4d4f;font-weight:bold' },
              yuan(scope.row.overdueRentAmount),
            ),
        },
        {
          name: 'overdueDepositAmount',
          columnProps: { label: '逾期押金', minWidth: 120, align: 'right' },
          render: (scope) =>
            h(
              'span',
              { style: 'color:#ff4d4f;font-weight:bold' },
              yuan(scope.row.overdueDepositAmount),
            ),
        },
        {
          name: 'payMode',
          columnProps: { label: '付款方式', minWidth: 110 },
          render: (scope) =>
            h(
              'el-tag',
              {
                props: {
                  type: payModeTagType(scope.row.payMode),
                  size: 'small',
                },
              },
              scope.row.payModeName || '--',
            ),
        },
        {
          name: 'vehicleManagerName',
          columnProps: {
            label: '车管',
            prop: 'vehicleManagerName',
            minWidth: 100,
          },
        },
        {
          name: 'driverManagerName',
          columnProps: {
            label: '司管',
            prop: 'driverManagerName',
            minWidth: 100,
          },
        },
        {
          name: 'rentStatus',
          columnProps: { label: '租赁状态', minWidth: 100 },
          render: (scope) =>
            h(
              'el-tag',
              {
                props: {
                  type: rentStatusTagType(scope.row.rentStatusName),
                  size: 'small',
                },
              },
              scope.row.rentStatusName || '--',
            ),
        },
        {
          name: 'operate',
          type: 'operate',
          columnProps: { label: '操作', width: 100, fixed: 'right' },
          getOperateList: ({ row }) => [
            { name: 'detail', label: '查看详情', action: () => onDetail(row) },
          ],
        },
      ],
    },
  }
  return configData
}

// 取当前筛选参数(经 dealWithSearch 处理)
function getSearchParams(getBlm) {
  const blm = getBlm && getBlm()
  return blm && blm.getParams ? blm.getParams() : {}
}
